import {CircularProgress} from '@mui/material';
import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {useMemo, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import {Link} from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import {MSG_INDENT_PL} from './constants';
import {AgentIcon, CheckIcon, ChevronIcon, UserIcon} from './icons';
import styles from './markdownProse.module.css';
import type {ChatMessage, ProgressStep, useWbAgentChat} from './useWbAgentChat';

type Chat = ReturnType<typeof useWbAgentChat>;

// ─── ToolCallBlock ──────────────────────────────────────────────────
const ToolCallBlock: React.FC<{steps: ProgressStep[]}> = ({steps}) => {
  const [expanded, setExpanded] = useState(false);
  const toolSteps = steps.filter(s => s.type === 'tool_call');
  if (toolSteps.length === 0) {
    return null;
  }
  return (
    <div className="my-[3px]">
      <span
        onClick={() => setExpanded(prev => !prev)}
        className="inline-flex cursor-pointer select-none items-center gap-[3px] text-[11px] text-[#8F949E] hover:text-[#676D78]">
        <ChevronIcon expanded={expanded} />
        {toolSteps.length} tool call{toolSteps.length !== 1 ? 's' : ''}
      </span>
      {expanded && (
        <div className="mt-[3px] pl-2">
          {toolSteps.map((step, j) => (
            <div
              key={j}
              className="flex items-baseline gap-4 overflow-hidden text-ellipsis whitespace-nowrap py-px">
              <span className="shrink-0 text-[11px] text-[#79808A]">
                {step.label}
              </span>
              {step.detail && (
                <span className="truncate text-[11px] text-[#B1B4B9]">
                  {step.detail}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── LiveActivityFeed ───────────────────────────────────────────────
const LiveActivityFeed: React.FC<{
  steps: ProgressStep[];
  onCancel?: () => void;
}> = ({steps, onCancel}) => {
  const recentSteps = steps.slice(-6);
  return (
    <div className="mb-2 mt-4">
      <div className="mb-[3px] flex items-center gap-6">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded bg-[#038194] text-white">
          <AgentIcon />
        </div>
        <span className="text-[12px] font-semibold text-[#2B3038]">
          WB Agent
        </span>
      </div>
      <div className={MSG_INDENT_PL}>
        {recentSteps.length === 0 ? (
          <div className="flex items-center gap-6">
            <CircularProgress size={10} thickness={5} sx={{color: '#13A9BA'}} />
            <span className="flex-1 text-[12px] text-[#8F949E]">
              Thinking...
            </span>
            {onCancel && (
              <span
                onClick={onCancel}
                className="cursor-pointer text-[11px] text-[#B1B4B9] hover:text-[#FF596D]">
                Stop
              </span>
            )}
          </div>
        ) : (
          <div>
            {recentSteps.map((step, j) => {
              const isLast = j === recentSteps.length - 1;
              return (
                <div
                  key={j}
                  className={`flex items-center gap-4 overflow-hidden text-ellipsis whitespace-nowrap py-px ${isLast ? 'opacity-100' : 'opacity-50'}`}>
                  {isLast ? (
                    <CircularProgress
                      size={9}
                      thickness={5}
                      sx={{color: '#13A9BA', flexShrink: 0}}
                    />
                  ) : (
                    <span className="flex h-[9px] w-[9px] shrink-0 items-center justify-center text-[#00B26E]">
                      <CheckIcon />
                    </span>
                  )}
                  <span
                    className={`shrink-0 text-[11px] ${isLast ? 'text-[#676D78]' : 'text-[#B1B4B9]'}`}>
                    {step.type === 'tool_call' ? step.label : 'Thinking'}
                  </span>
                  {step.detail && (
                    <span className="truncate text-[11px] text-[#B1B4B9]">
                      {step.detail}
                    </span>
                  )}
                </div>
              );
            })}
            {onCancel && (
              <div className="mt-2">
                <span
                  onClick={onCancel}
                  className="cursor-pointer text-[11px] text-[#B1B4B9] hover:text-[#FF596D]">
                  Stop
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MessagesTab ────────────────────────────────────────────────────
export const MessagesTab: React.FC<{
  chat: Chat;
  isOpen: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}> = ({chat, isOpen, messagesContainerRef}) => {
  const mdComponents = useMemo(
    () => ({
      a: ({href, children}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
        const isInternal =
          href &&
          (href.startsWith('/') || href.startsWith(window.location.origin));
        if (isInternal) {
          const path = href.startsWith('/')
            ? href
            : href.slice(window.location.origin.length);
          return <Link to={path}>{children}</Link>;
        }
        return <TargetBlank href={href}>{children}</TargetBlank>;
      },
    }),
    []
  );

  return (
    <div ref={messagesContainerRef} className="flex-1 overflow-auto p-12">
      {chat.available === null && isOpen && (
        <div className="flex justify-center pt-48">
          <CircularProgress size={20} sx={{color: '#13A9BA'}} />
        </div>
      )}
      {chat.available === false && (
        <div className="pt-48 text-center text-[#8F949E]">
          <p className="text-[13px]">
            WB Agent is not available for this project.
          </p>
        </div>
      )}
      {chat.available === true && chat.messages.length === 0 && (
        <div className="pt-48 text-center text-[#8F949E]">
          <p className="text-[13px]">Send a message to start a conversation.</p>
        </div>
      )}
      {chat.messages.map((msg, i) => {
        if (msg.role === 'steps') {
          return null;
        }
        if (msg.role === 'loading') {
          return (
            <LiveActivityFeed
              key={i}
              steps={msg.steps}
              onCancel={chat.cancel}
            />
          );
        }

        const precedingSteps =
          msg.role === 'agent' && i > 0 && chat.messages[i - 1].role === 'steps'
            ? (chat.messages[i - 1] as ChatMessage & {role: 'steps'}).steps
            : null;

        if (msg.role === 'user') {
          const prev = chat.messages[i - 1];
          const isNewTurn = prev && prev.role !== 'loading';
          return (
            <div key={i} className={`mb-2 ${isNewTurn ? 'mt-12' : 'mt-0'}`}>
              <div className="mb-[3px] flex items-center gap-6">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded bg-[#D4D5D9] text-white">
                  <UserIcon />
                </div>
                <span className="text-[12px] font-semibold text-[#2B3038]">
                  You
                </span>
              </div>
              <div className={MSG_INDENT_PL}>
                <p className="whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-[#2B3038]">
                  {msg.text}
                </p>
              </div>
            </div>
          );
        }

        // Agent message
        return (
          <div key={i} className="mb-2 mt-6">
            <div className="mb-[3px] flex items-center gap-6">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded bg-[#038194] text-white">
                <AgentIcon />
              </div>
              <span className="text-[12px] font-semibold text-[#2B3038]">
                WB Agent
              </span>
            </div>
            <div className={MSG_INDENT_PL}>
              {precedingSteps && precedingSteps.length > 0 && (
                <ToolCallBlock steps={precedingSteps} />
              )}
              <div className={styles.markdownProse}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}>
                  {msg.text}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
